/**
 * Single source of truth for the API origin (docs/engineering/adr/0001-tenant-hostnames.md).
 *
 *   1. Behind forge-edge (the shared app and every tenant custom domain)
 *      index.html carries <meta name="forge-api-base" content="">: the API is
 *      same-origin, so session cookies stay first-party on that hostname.
 *   2. Otherwise VITE_API_URL: the legacy direct setup (an app host
 *      on Pages calling the API host cross-origin) and *.pages.dev previews.
 *   3. Local dev: both empty, and the Vite proxy serves `/api`.
 *
 * One build serves every case, so moving a hostname behind the edge needs no
 * rebuild. Kept free of axios/store imports so it can be used anywhere (and
 * survives tests that mock `@/lib/api`).
 */
export function resolveApiBase(doc: Pick<Document, 'querySelector'> | null, buildTime: string | undefined): string {
  const meta = doc?.querySelector<HTMLMetaElement>('meta[name="forge-api-base"]')
  const base = meta ? meta.content : (buildTime || '')
  return base.replace(/\/+$/, '')
}

export const API_BASE: string = resolveApiBase(
  typeof document === 'undefined' ? null : document,
  import.meta.env.VITE_API_URL as string | undefined,
)

/** Prefix an API-relative path (`/api/...`, `/uploads/...`) with the API origin. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Resolve a URL returned by the backend. Absolute (S3/CDN), blob:, and data:
 * URLs pass through; server-relative paths (local-disk uploads such as
 * `/uploads/...` or `uploads/...`) are resolved against the API origin.
 */
export function resolveApiUrl(url: string): string
export function resolveApiUrl(url: string | null | undefined): string | undefined
export function resolveApiUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  if (/^(https?:|blob:|data:)/i.test(url)) return url
  return apiUrl(url)
}
